import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft } from 'lucide-react';

import { cn } from '@/lib/cn';
import { useHaptics } from '@/hooks/useHaptics';
import { useMainButton } from '@/hooks/useMainButton';

import { OptionGrid } from './OptionGrid';
import { ARCHETYPES, VIBES, ACCENTS, MAX_ACCENTS } from './catalog';
import { composePrompt, PROMPT_MAX } from './composePrompt';
import { STEP_ORDER, type AssistantSelections, type AssistantStep, type Option } from './types';

/**
 * Avatar Generation Assistant — a guided, local-only character builder.
 *
 * Helps users who don't know what to write: pick Archetype → Vibe → Accents,
 * watch a natural-language prompt assemble, edit it, then hand it to
 * `AvatarCreate` (via router `location.state`) which pre-fills its textarea.
 *
 * AppShell child route: navigation is driven by the per-step MainButton
 * ("Next" / "Create avatar") + an in-page Back button. The Telegram BackButton
 * (wired at AppShell level) exits the whole wizard — by design.
 */
export function AvatarAssistant() {
  const navigate = useNavigate();
  const haptics = useHaptics();
  const { t } = useTranslation('avatarAssistant');

  const [step, setStep] = useState<AssistantStep>('archetype');
  const [selections, setSelections] = useState<AssistantSelections>({ accentIds: [] });
  const [reviewPrompt, setReviewPrompt] = useState('');

  const stepIndex = STEP_ORDER.indexOf(step);
  const isReview = step === 'review';

  const display = useCallback(
    (opts: readonly Option[]) => opts.map((o) => ({ id: o.id, label: t(o.labelKey), icon: o.icon })),
    [t],
  );

  const toggleArchetype = useCallback((id: string) => {
    setSelections((s) => ({ ...s, archetypeId: s.archetypeId === id ? undefined : id }));
  }, []);

  const toggleVibe = useCallback((id: string) => {
    setSelections((s) => ({ ...s, vibeId: s.vibeId === id ? undefined : id }));
  }, []);

  const toggleAccent = useCallback((id: string) => {
    setSelections((s) => {
      if (s.accentIds.includes(id)) {
        return { ...s, accentIds: s.accentIds.filter((x) => x !== id) };
      }
      if (s.accentIds.length >= MAX_ACCENTS) return s; // cap reached
      return { ...s, accentIds: [...s.accentIds, id] };
    });
  }, []);

  const stepValid = (s: AssistantStep): boolean => {
    if (s === 'archetype') return !!selections.archetypeId;
    if (s === 'vibe') return !!selections.vibeId;
    return true; // accents optional; review validity is gated on prompt length
  };

  const goNext = () => {
    const next = STEP_ORDER[stepIndex + 1];
    if (!next) return;
    // Seed the editable Review prompt on forward entry (re-derives from the
    // latest selections; manual edits made while on Review are not touched
    // until the user steps back and forward again). This is the Phase-2 seam:
    // an async enhancePrompt() can wrap composePrompt(...) here later.
    if (next === 'review') setReviewPrompt(composePrompt(selections));
    haptics.impact('light');
    setStep(next);
  };

  const goBack = () => {
    if (stepIndex <= 0) {
      haptics.impact('light');
      navigate(-1); // exit the wizard from the first step
      return;
    }
    haptics.impact('light');
    setStep(STEP_ORDER[stepIndex - 1]);
  };

  const handleFinish = () => {
    const finalPrompt = reviewPrompt.trim().slice(0, PROMPT_MAX);
    if (!finalPrompt) return;
    haptics.impact('medium');
    navigate('/avatars/new', { state: { assistantPrompt: finalPrompt } });
  };

  useMainButton({
    text: isReview ? t('cta.create') : t('cta.next'),
    enabled: isReview ? reviewPrompt.trim().length > 0 : stepValid(step),
    onClick: isReview ? handleFinish : goNext,
  });

  return (
    <div className="flex flex-col gap-5 px-4 pb-28 pt-4">
      <header className="flex items-center gap-3">
        <button
          type="button"
          aria-label={t('back')}
          onClick={goBack}
          className="shrink-0 rounded-full bg-bg-secondary p-2 text-fg transition-opacity active:opacity-80"
        >
          <ChevronLeft aria-hidden className="h-4 w-4" />
        </button>
        <div className="flex flex-1 flex-col gap-1.5">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-fg-muted">
            {t('progress', { current: stepIndex + 1, total: STEP_ORDER.length })} · {t(`steps.${step}.label`)}
          </p>
          <div className="flex items-center gap-1.5">
            {STEP_ORDER.map((s, i) => (
              <span
                key={s}
                aria-current={i === stepIndex || undefined}
                className={cn(
                  'h-2 w-2 rounded-full transition-colors',
                  i <= stepIndex ? 'bg-brand' : 'bg-bg-tertiary',
                  i === stepIndex && 'ring-2 ring-brand/30',
                )}
              />
            ))}
          </div>
        </div>
      </header>

      {step === 'archetype' && (
        <section className="flex flex-col gap-3">
          <StepHeader title={t('steps.archetype.title')} subtitle={t('steps.archetype.subtitle')} />
          <OptionGrid
            options={display(ARCHETYPES)}
            selectedIds={selections.archetypeId ? [selections.archetypeId] : []}
            onToggle={toggleArchetype}
          />
        </section>
      )}

      {step === 'vibe' && (
        <section className="flex flex-col gap-3">
          <StepHeader title={t('steps.vibe.title')} subtitle={t('steps.vibe.subtitle')} />
          <OptionGrid
            options={display(VIBES)}
            selectedIds={selections.vibeId ? [selections.vibeId] : []}
            onToggle={toggleVibe}
          />
        </section>
      )}

      {step === 'accents' && (
        <section className="flex flex-col gap-3">
          <StepHeader
            title={t('steps.accents.title')}
            subtitle={t('steps.accents.subtitle', { max: MAX_ACCENTS })}
          />
          <p className="self-end text-[11px] text-fg-muted">
            {selections.accentIds.length}/{MAX_ACCENTS}
          </p>
          <OptionGrid
            options={display(ACCENTS)}
            selectedIds={selections.accentIds}
            onToggle={toggleAccent}
            isDisabled={() => selections.accentIds.length >= MAX_ACCENTS}
          />
        </section>
      )}

      {step === 'review' && (
        <section className="flex flex-col gap-3">
          <StepHeader title={t('steps.review.title')} subtitle={t('steps.review.subtitle')} />
          <textarea
            id="assistant-prompt"
            value={reviewPrompt}
            onChange={(e) => setReviewPrompt(e.target.value.slice(0, PROMPT_MAX))}
            rows={5}
            className={cn(
              'w-full resize-y rounded-xl bg-bg-secondary p-3 text-sm text-fg placeholder:text-fg-muted',
              'border border-border focus:border-brand focus:outline-none',
            )}
          />
          <p className="self-end text-[11px] text-fg-muted">
            {reviewPrompt.length}/{PROMPT_MAX}
          </p>
          <p className="text-[11px] leading-relaxed text-fg-muted">{t('steps.review.note')}</p>
        </section>
      )}
    </div>
  );
}

function StepHeader({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <header className="flex flex-col gap-1">
      <h1 className="text-xl font-bold text-fg">{title}</h1>
      <p className="text-sm text-fg-muted">{subtitle}</p>
    </header>
  );
}
